import { useState, useEffect } from "react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { getEmailProcesses, updateEmailProcess, getEmailRecipients, addEmailRecipient, updateEmailRecipient, deleteEmailRecipient, createEmailProcess, deleteEmailProcess, sendNotificationEmail } from "../../lib/api";
import { DEFAULT_LOCALE, PLACEHOLDER_DOMAIN, EMPTY_EMAIL_PROCESSES, EMPTY_EMAIL_RECIPIENTS, TOAST_SAVED, TOAST_SAVE_ERROR, TOAST_ADDED, TOAST_ADD_ERROR, TOAST_REMOVED, TOAST_REMOVE_ERROR, TOAST_UPDATED, TOAST_EMAIL_SENT, TOAST_EMAIL_ERROR, TOAST_NO_PERMISSION, TOAST_REQUIRED, TOAST_LOAD_ERROR, PLH_EMAIL_PROCESS_FROM, PLH_EMAIL_SUBJECT, PLH_EMAIL_FROM, PLH_EMAIL_RECIPIENT, PLH_EMAIL_GROUP, PLH_EMAIL_HTML, PLH_EMAIL_TEST, PLH_PROCESS_NAME, PLH_PROCESS_LABEL } from "../../lib/constants";
import { PROCESS_VARIABLES } from "../../lib/tabs";
import { Save, Mail, Code, Plus, Trash2, ToggleRight, ToggleLeft, ChevronDown, ChevronRight, Eye, Send, Loader2 } from "lucide-react";
import { logger } from "../../lib/logger";


export default function AdminEmailSettings({ readOnly = false }) {
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedProcess, setExpandedProcess] = useState(null);
  const [showTemplate, setShowTemplate] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newProcessKey, setNewProcessKey] = useState("");
  const [newProcessLabel, setNewProcessLabel] = useState("");
  const [showAddProcess, setShowAddProcess] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testingProcess, setTestingProcess] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const processesData = await getEmailProcesses();
        setProcesses(processesData.map(p => ({ ...p, recipients: p.recipients || [] })));
      } catch (err) {
        logger.error(err);
        toast.error(TOAST_LOAD_ERROR);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleSaveProcess(process) {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    setSaving(true);
    try {
      const updated = await updateEmailProcess(process.uid, {
        enabled: process.enabled,
        email_subject: process.email_subject,
        email_from_name: process.email_from_name,
        email_from_address: process.email_from_address,
        email_template: process.email_template
      });
      setProcesses((p) => p.map((item) => item.uid === process.uid ? { ...item, ...updated } : item));
      toast.success(TOAST_SAVED);
    } catch (err) {
      logger.error(err);
      toast.error(TOAST_SAVE_ERROR);
    } finally {
      setSaving(false);
    }
  }

  async function handleAddRecipient(proc) {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    if (!newEmail.trim()) return toast.error(TOAST_REQUIRED);
    try {
      const added = await addEmailRecipient({ process_id: proc.id, email: newEmail.trim(), label: newLabel.trim() || newEmail.trim(), enabled: true });
      setProcesses((prev) =>
        prev.map((p) =>
          p.uid === proc.uid ? { ...p, recipients: [...p.recipients, added] } : p
        )
      );
      setNewEmail("");
      setNewLabel("");
      toast.success(TOAST_ADDED);
    } catch (err) {
      logger.error(err);
      toast.error(TOAST_ADD_ERROR);
    }
  }

  async function handleToggleRecipient(proc, recipient) {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    try {
      await updateEmailRecipient(recipient.uid, { email: recipient.email, label: recipient.label, enabled: !recipient.enabled });
      setProcesses((prev) =>
        prev.map((p) =>
          p.uid === proc.uid ? { ...p, recipients: p.recipients.map((r) => r.uid === recipient.uid ? { ...r, enabled: !r.enabled } : r) } : p
        )
      );
    } catch (err) {
      logger.error(err);
      toast.error(TOAST_SAVE_ERROR);
    }
  }

  async function handleDeleteRecipient(proc, id) {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    try {
      await deleteEmailRecipient(id);
      setProcesses((prev) =>
        prev.map((p) =>
          p.uid === proc.uid ? { ...p, recipients: p.recipients.filter((r) => r.uid !== id) } : p
        )
      );
      toast.success(TOAST_REMOVED);
    } catch (err) {
      logger.error(err);
      toast.error(TOAST_REMOVE_ERROR);
    }
  }

  return (
    <div data-id="admin-email-settings" className="space-y-8">
      {/* Header */}
      <div className="bg-white border border-fendi p-4 md:p-8 max-w-4xl shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
          <p className="eyebrow flex items-center gap-2 text-champagne">
            <Mail size={16} /> Processos de Email
          </p>
          <button
            type="button"
            onClick={() => setShowAddProcess(!showAddProcess)}
            className="text-xs text-champagne hover:text-softblack transition-colors flex items-center gap-1 self-start sm:self-auto"
          >
            <Plus size={12} /> Novo processo
          </button>
        </div>
        <p className="text-xs text-warmgray">Cada processo tem seu próprio template, assunto e destinatários</p>
      </div>

      {/* Add new process */}
      {showAddProcess && (
        <div className="bg-white border border-fendi p-4 md:p-8 max-w-4xl shadow-sm">
          <p className="eyebrow mb-6 text-champagne">Novo Processo</p>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
            <label className="flex-1 block">
              <span className="eyebrow text-[10px] text-warmgray">Chave (única)</span>
              <input className="editorial-field mt-3" value={newProcessKey} onChange={(e) => setNewProcessKey(e.target.value)} placeholder={PLH_PROCESS_NAME} />
            </label>
            <label className="flex-1 block">
              <span className="eyebrow text-[10px] text-warmgray">Nome</span>
              <input className="editorial-field mt-3" value={newProcessLabel} onChange={(e) => setNewProcessLabel(e.target.value)} placeholder={PLH_PROCESS_LABEL} />
            </label>
            {!readOnly && <button type="button" onClick={handleCreateProcess} className="btn-primary !px-4 !py-3 h-[50px] w-full sm:w-auto"><Plus size={16} /></button>}
          </div>
        </div>
      )}

      {/* Process list */}
      {processes.length === 0 && (
        <div className="bg-white border border-fendi p-8 max-w-4xl shadow-sm text-center text-warmgray text-sm">
          {EMPTY_EMAIL_PROCESSES}
        </div>
      )}

      {processes.map((proc) => {
        const isExpanded = expandedProcess === proc.uid;
        const variables = PROCESS_VARIABLES[proc.process_key] || ["{name}", "{email}", "{phone}", "{service}", "{message}", "{contactMethod}", "{date}"];

        return (
          <div key={proc.uid} className="bg-white border border-fendi max-w-4xl shadow-sm overflow-hidden">
            {/* Process header */}
            <button
              type="button"
              onClick={() => setExpandedProcess(isExpanded ? null : proc.uid)}
              className="w-full flex items-center justify-between p-4 md:p-6 hover:bg-fendi/5 transition-colors"
            >
              <div className="flex items-center gap-3 md:gap-4 min-w-0">
                <div className={`w-2 h-2 rounded-full shrink-0 ${proc.enabled ? 'bg-champagne' : 'bg-fendi'}`} />
                <div className="text-left min-w-0">
                  <p className="text-sm font-medium text-softblack truncate">{proc.label}</p>
                  <p className="text-[11px] md:text-xs text-warmgray font-mono truncate">{proc.process_key}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 md:gap-3 shrink-0">
                <span className="text-[10px] md:text-xs text-warmgray whitespace-nowrap">{proc.recipients?.length || 0}</span>
                {isExpanded ? <ChevronDown size={14} className="text-warmgray" /> : <ChevronRight size={14} className="text-warmgray" />}
              </div>
            </button>

            {/* Expanded content */}
            {isExpanded && (
              <div className="border-t border-fendi/20 p-4 md:p-6 space-y-5 md:space-y-6">
                {/* Toggle + delete */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <label className={`relative inline-flex items-center ${readOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                      <input type="checkbox" checked={proc.enabled || false} onChange={(e) => updateProcess(proc.uid, "enabled", e.target.checked)} className="sr-only peer" disabled={readOnly} />
                      <div className="w-11 h-6 bg-fendi peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-champagne"></div>
                    </label>
                    <span className="text-xs text-warmgray">{proc.enabled ? 'Ativado' : 'Desativado'}</span>
                  </div>
                  {!readOnly && (
                    <button type="button" onClick={() => handleDeleteProcess(proc.uid)} className="text-xs text-warmgray hover:text-destructive transition-colors flex items-center gap-1">
                      <Trash2 size={12} /> Remover
                    </button>
                  )}
                </div>

                {/* Subject */}
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Assunto</span>
                  <input className="editorial-field mt-3" value={proc.email_subject || ""} onChange={(e) => updateProcess(proc.uid, "email_subject", e.target.value)} placeholder={PLH_EMAIL_SUBJECT} disabled={readOnly} />
                </label>

                {/* From name / address */}
                <div className="grid md:grid-cols-2 gap-8">
                  <label className="block">
                    <span className="eyebrow text-[10px] text-warmgray">Nome do Remetente</span>
                    <input className="editorial-field mt-3" value={proc.email_from_name || ""} onChange={(e) => updateProcess(proc.uid, "email_from_name", e.target.value)} placeholder={PLH_EMAIL_FROM} disabled={readOnly} />
                  </label>
                  <label className="block">
                    <span className="eyebrow text-[10px] text-warmgray">Email do Remetente</span>
                    <input className="editorial-field mt-3" value={proc.email_from_address || ""} onChange={(e) => updateProcess(proc.uid, "email_from_address", e.target.value)} placeholder={`noreply@${PLACEHOLDER_DOMAIN}`} disabled={readOnly} />
                  </label>
                </div>

                {/* Recipients */}
                <div className="border-t border-fendi/10 pt-6">
                  <p className="eyebrow mb-4 text-champagne">Destinatários</p>
                  <div className="flex flex-col sm:flex-row gap-3 mb-4 items-stretch sm:items-end">
                    <label className="flex-1 block">
                      <span className="eyebrow text-[10px] text-warmgray">Email</span>
                      <input className="editorial-field mt-3" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder={PLH_EMAIL_RECIPIENT} onKeyDown={(e) => e.key === "Enter" && handleAddRecipient(proc)} disabled={readOnly} />
                    </label>
                    <label className="flex-1 block">
                      <span className="eyebrow text-[10px] text-warmgray">Label</span>
                      <input className="editorial-field mt-3" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder={PLH_EMAIL_GROUP} onKeyDown={(e) => e.key === "Enter" && handleAddRecipient(proc)} disabled={readOnly} />
                    </label>
                    {!readOnly && <button type="button" onClick={() => handleAddRecipient(proc)} className="btn-primary !px-4 !py-3 h-[50px] w-full sm:w-auto"><Plus size={16} /></button>}
                  </div>
                  <div className="space-y-2">
                    {(proc.recipients || []).length === 0 && <p className="text-xs text-warmgray text-center py-4">{EMPTY_EMAIL_RECIPIENTS}</p>}
                    {(proc.recipients || []).map((r) => (
                      <div key={r.uid} className={`flex items-center justify-between p-3 border transition-colors ${r.enabled ? "border-fendi/30 bg-white" : "border-fendi/10 bg-fendi/5 opacity-60"}`}>
                        <div className="flex items-center gap-3">
                          {!readOnly && (
                            <button type="button" onClick={() => handleToggleRecipient(proc, r)} className="text-warmgray hover:text-softblack">
                              {r.enabled ? <ToggleRight size={20} className="text-champagne" /> : <ToggleLeft size={20} />}
                            </button>
                          )}
                          <div>
                            <p className="text-sm text-softblack">{r.email}</p>
                            {r.label && <p className="text-[10px] text-warmgray">{r.label}</p>}
                          </div>
                        </div>
                        {!readOnly && <button type="button" onClick={() => handleDeleteRecipient(proc, r.uid)} className="text-warmgray hover:text-destructive p-1"><Trash2 size={14} /></button>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Template */}
                <div className="border-t border-fendi/10 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <p className="eyebrow text-champagne">Template HTML</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setShowTemplate("code")}
                        className={`text-xs px-3 py-1.5 border transition-colors ${showTemplate === "code" ? "bg-champagne/10 border-champagne text-softblack" : "border-fendi text-warmgray hover:text-softblack"}`}
                      >
                        <Code size={12} className="inline mr-1" /> Código
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowTemplate("design")}
                        className={`text-xs px-3 py-1.5 border transition-colors ${showTemplate === "design" ? "bg-champagne/10 border-champagne text-softblack" : "border-fendi text-warmgray hover:text-softblack"}`}
                      >
                        <Eye size={12} className="inline mr-1" /> Design
                      </button>
                    </div>
                  </div>

                  {showTemplate === "code" && (
                    <div className="space-y-3">
                      <p className="text-[10px] text-warmgray">Variáveis: {variables.map((v) => <code key={v} className="bg-fendi/20 px-1 py-0.5 rounded mr-1">{v}</code>)}</p>
                      <textarea className="editorial-field font-mono text-[10px] leading-relaxed" rows={16} value={proc.email_template || ""} onChange={(e) => updateProcess(proc.uid, "email_template", e.target.value)} placeholder={PLH_EMAIL_HTML} disabled={readOnly} />
                    </div>
                  )}

                  {showTemplate === "design" && (
                    <div className="space-y-3">
                      <p className="text-[10px] text-warmgray">Pré-visualização com dados de exemplo</p>
                      <div className="border border-fendi/30 rounded-lg overflow-hidden bg-gray-50">
                        <div
                          className="w-full"
                          style={{ minHeight: "400px" }}
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize((proc.email_template || '')
                              .replace(/\{name\}/g, 'João Silva')
                              .replace(/\{email\}/g, 'joao@exemplo.com')
                              .replace(/\{phone\}/g, '+351 912 345 678')
                              .replace(/\{service\}/g, 'Consultoria Imobiliária')
                              .replace(/\{message\}/g, 'Olá, gostaria de saber mais sobre os serviços disponíveis.')
                              .replace(/\{contactMethod\}/g, 'WhatsApp')
                              .replace(/\{date\}/g, new Date().toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })))
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Save */}
                <div className="flex justify-between items-center pt-4 border-t border-fendi/10">
                  <div className="flex items-center gap-3">
                    <input
                      className="editorial-field !py-2 !text-sm max-w-[260px]"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder={PLH_EMAIL_TEST}
                      onKeyDown={(e) => e.key === "Enter" && handleTestSend(proc)}
                    />
                    <button
                      type="button"
                      onClick={() => handleTestSend(proc)}
                      disabled={testingProcess === proc.uid}
                      className="text-xs flex items-center gap-1 px-4 py-2 border border-fendi/40 text-warmgray hover:text-softblack hover:border-fendi transition-colors disabled:opacity-50"
                    >
                      {testingProcess === proc.uid ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                      Testar Envio
                    </button>
                  </div>
                  {!readOnly && (
                    <button onClick={() => handleSaveProcess(proc)} disabled={saving} className="btn-primary !px-10">
                      <Save size={14} className="mr-3" /> {saving ? "A guardar..." : "Guardar"}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
